import type { PluralCategory, PluralRecord } from './types'

const PLURAL_CATEGORIES: readonly string[] = ['zero', 'one', 'two', 'few', 'many', 'other']

/** True for `{ one: '…', other: '…' }`; false for a nested group. Mirrors the
 * `IsPluralRecord<T>` type test exactly — the two must not be able to disagree,
 * because the type decides whether a leaf is a function and the runtime decides
 * what to call. */
export function isPluralRecord(value: unknown): value is PluralRecord {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false
  const entries = Object.entries(value as Record<string, unknown>)
  if (entries.length === 0) return false
  if (!entries.some(([key]) => key === 'other')) return false
  return entries.every(([key, leaf]) => PLURAL_CATEGORIES.includes(key) && typeof leaf === 'string')
}

const pluralRules = new Map<string, Intl.PluralRules>()

function rulesFor(locale: string): Intl.PluralRules {
  let rules = pluralRules.get(locale)
  if (!rules) {
    rules = new Intl.PluralRules(locale)
    pluralRules.set(locale, rules)
  }
  return rules
}

/**
 * Pick the form for `count` in `locale`.
 *
 * Falls back to `other` when the locale asks for a category this record does
 * not carry — which is the normal case, not an error: an English record has
 * `one`/`other` and Polish asks for `few`, so a Polish translation that has not
 * been written yet resolves through the English record's `other`. Rendering
 * nothing there would be worse than rendering an imperfectly-inflected string.
 */
export function selectPlural(record: PluralRecord, count: number, locale: string): string {
  const category = rulesFor(locale).select(count) as PluralCategory
  return record[category] ?? record.other
}

const PLACEHOLDER = /\{([^{}\s]+)\}/g

/**
 * Substitute `{name}` placeholders.
 *
 * A placeholder with no supplied value is left **verbatim** rather than
 * replaced with an empty string. `Saved by {name}` is a visible, reportable
 * defect; `Saved by ` reads as a real record with a missing author and gets
 * believed.
 */
export function formatMessage(template: string, values?: Readonly<Record<string, string | number>>): string {
  if (!values) return template
  return template.replace(PLACEHOLDER, (match, name: string) => {
    const value = values[name]
    return value === undefined ? match : String(value)
  })
}

export interface MessageSlot {
  /** Positional slot index from `<0>…</0>`. */
  readonly index: number
  /** The text between the tags, already interpolated. */
  readonly text: string
}

export type MessagePart = string | MessageSlot

const SLOT = /<(\d+)>([\s\S]*?)<\/\1>/g

/**
 * Split `'By continuing you accept the <0>terms</0>.'` into
 * `['By continuing you accept the ', {index: 0, text: 'terms'}, '.']`.
 *
 * Positional integer slots, and **no HTML parsing whatsoever** — the runtime
 * never sees a tag name, never builds an element from catalogue content, and
 * `dangerouslySetInnerHTML` appears nowhere in this package. A catalogue is
 * reviewed source, but it is also the file a future translation vendor edits,
 * and "the translator can inject markup" is not a property worth having.
 *
 * The backreference `<\/\1>` is what keeps `<0>a</1>` from matching: an
 * unbalanced slot stays literal text, which is visible, instead of swallowing
 * the rest of the message.
 */
export function splitMessage(message: string): MessagePart[] {
  const parts: MessagePart[] = []
  let cursor = 0
  SLOT.lastIndex = 0
  let match = SLOT.exec(message)
  while (match) {
    if (match.index > cursor) parts.push(message.slice(cursor, match.index))
    parts.push({ index: Number(match[1]), text: match[2] ?? '' })
    cursor = match.index + match[0].length
    match = SLOT.exec(message)
  }
  if (cursor < message.length) parts.push(message.slice(cursor))
  return parts
}
